"use client"

import type { UserModel } from '@/domain/models/user.model';
import type React from 'react';
import { createContext, useState, useContext, type ReactNode, useEffect, useCallback, useMemo } from 'react'
import type { Notification } from "@/domain/entities/notification";
import type { UserDTO } from '@/domain/dto/user.dto';
import { fetchUsers } from '@/features/users/services/user-service';
import { fetchRolesPermissions } from '@/features/roles/services/role-service';
import { useQuery } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import type { RolePermission } from '@/domain/dto/role-permission.dto';
import { useRouter, usePathname } from 'next/navigation';
import { Paths } from '@/features/core/navigation/constants';

interface Permissions {
    canNewProject1: boolean;
    canNewProject2: boolean;
    canNewUser: boolean;
    canNewRole: boolean;
    canNewTask: boolean;
    canEditTask: boolean;
    canDeleteTask: boolean;
    canSettlement: boolean;
}

// Tipo del contexto que contiene el estado del usuario
interface UserContextType {
    user: UserModel | null;
    setUser: React.Dispatch<React.SetStateAction<UserModel | null>>;
    notifications: Notification[];
    setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
    users: UserDTO[]; // Nuevo estado para los usuarios
    setUsers: React.Dispatch<React.SetStateAction<UserDTO[]>>; // Función para actualizar usuarios
    refreshUsers: () => void; // Función para recargar la lista de usuarios
    roles: RolePermission[]; // Nuevo estado para los usuarios
    setRoles: React.Dispatch<React.SetStateAction<RolePermission[]>>; // Función para actualizar usuarios
    refreshRoles: () => void; // Función para recargar la lista de usuarios
    userCurrentPermissions: RolePermission[];
    permissionsLoaded: boolean; // Flag que indica si los permisos han sido cargados
    permissions: Permissions;
}

// Crear el contexto de usuario
const UserContext = createContext<UserContextType | undefined>(undefined);
// const fetcher = (url: string) => fetch(url).then((res) => res.json());
const apiUrl =
    process.env.NEXT_PUBLIC_API_URL || "https://localhost:7061";

// Claves para sessionStorage
const USER_STORAGE_KEY = 'user_fallback_data';

// Importar funciones de cifrado
import { encryptStorage, decryptStorage } from '@/lib/crypto.utils';

/**
 * Guarda datos del usuario en sessionStorage CIFRADOS
 */
function saveUserToStorage(user: UserModel | null) {
    if (typeof window === 'undefined') return;

    try {
        if (user) {
            const jsonString = JSON.stringify(user);
            const encrypted = encryptStorage(jsonString);
            sessionStorage.setItem(USER_STORAGE_KEY, encrypted);
        } else {
            sessionStorage.removeItem(USER_STORAGE_KEY);
        }
    } catch (error) {
        console.error('Error saving user to storage:', error);
        sessionStorage.removeItem(USER_STORAGE_KEY);
    }
}

/**
 * Recupera y DESCIFRA datos del usuario desde sessionStorage
 */
function getUserFromStorage(): UserModel | null {
    if (typeof window === 'undefined') return null;

    try {
        const encrypted = sessionStorage.getItem(USER_STORAGE_KEY);
        if (!encrypted) return null;

        const decrypted = decryptStorage(encrypted);
        return JSON.parse(decrypted);
    } catch (error) {
        console.error('Error reading/decrypting user from storage:', error);
        sessionStorage.removeItem(USER_STORAGE_KEY);
        return null;
    }
}

/**
 * Limpia completamente el sessionStorage del usuario
 */
function clearUserStorage() {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem(USER_STORAGE_KEY);
}

/**
 * Proveedor del contexto de usuario.
 * Permite acceder y modificar el estado del usuario a lo largo de la aplicación.
 * 
 * @param children Componentes hijos que podrán consumir el contexto.
 * @param initialUser Datos iniciales del usuario, pasados desde el servidor.
 */
export const UserProvider = ({ children, initialUser, shouldClearSession, isNetworkError }: {
    children: ReactNode;
    initialUser: UserModel | null;
    shouldClearSession?: boolean;
    isNetworkError?: boolean;
}) => {
    const router = useRouter();
    const pathname = usePathname();

    // Determinar el usuario inicial
    const [user, setUser] = useState<UserModel | null>(() => {
        if (initialUser) {
            saveUserToStorage(initialUser);
            return initialUser;
        }

        if (shouldClearSession) {
            clearUserStorage();
            return null;
        }

        if (isNetworkError) {
            const fallbackUser = getUserFromStorage();
            if (fallbackUser) {
                console.warn('Using fallback user data due to network error');
                return fallbackUser;
            }
        }

        clearUserStorage();
        return null;
    });

    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [users, setUsers] = useState<UserDTO[]>([]); // Estado de usuarios
    const [roles, setRoles] = useState<RolePermission[]>([]); // Estado de roles
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);
    const [userCurrentPermissions, setUserCurrentPermissions] = useState<RolePermission[]>([]);

    // Manejar redirección cuando shouldClearSession es true
    useEffect(() => {
        if (shouldClearSession && pathname !== Paths.Login) {
            clearUserStorage();
            // Llamar al endpoint de logout para borrar la cookie del servidor
            fetch('/api/v2/logout', { method: 'POST' })
                .then(() => {
                    router.push(Paths.Login);
                })
                .catch((error) => {
                    console.error('Error during logout:', error);
                    router.push(Paths.Login);
                });
        }
    }, [shouldClearSession, pathname, router]);

    // Actualizar storage cuando el usuario cambie
    useEffect(() => {
        if (user) {
            saveUserToStorage(user);
        } else {
            clearUserStorage();
        }
    }, [user]);

    // Condición para ejecutar las queries (si el usuario existe y tiene id válida)
    const shouldFetch = useMemo(() => !!(user && user.id_user > 0), [user]);

    // Query para notificaciones
    const {
        data: notificationsData,
        refetch: refetchNotifications,
    } = useQuery<Notification[]>({
        queryKey: ['notifications', user?.id_user],
        queryFn: async () => {
            const res = await fetch(`${apiUrl}/api/v1.0/notification/user/${user?.id_user ?? -1}`);
            if (!res.ok) throw new Error('Error al obtener notificaciones');
            return res.json() as Promise<Notification[]>;
        },
        enabled: shouldFetch,
        refetchInterval: 10000, // Se revalida cada 5 segundos (5000ms)
        staleTime: Number.POSITIVE_INFINITY, // la data no expira
    });

    // Query para permisos
    const {
        data: permissionsData,
        refetch: refetchPermissions,
    } = useQuery<RolePermission[]>({
        queryKey: ['permissions', user?.id_user],
        queryFn: async () => {
            const res = await fetch(`${apiUrl}/api/v1.0/security/permissions/user/${user?.id_user ?? -1}`);
            if (!res.ok) throw new Error('Error al obtener permisos');
            const body = await res.json();

            return body as RolePermission[];
        },
        enabled: shouldFetch,
        // refetchInterval: 10000,
        staleTime: Number.POSITIVE_INFINITY,
    });

    // Actualiza las notificaciones solo si han cambiado (comparación profunda)
    useEffect(() => {
        if (notificationsData && !isEqual(notificationsData, notifications)) {
            setNotifications(notificationsData);
        }
    }, [notificationsData, notifications]);

    // Actualiza los permisos del usuario actual solo si han cambiado
    useEffect(() => {
        if (user && user.id_user > 0 && permissionsData && !isEqual(permissionsData, userCurrentPermissions)) {
            setUserCurrentPermissions(permissionsData);
            setPermissionsLoaded(true);
        }
    }, [user, permissionsData, userCurrentPermissions]);

    // Función para refrescar permisos manualmente
    // Memoiza la función para evitar recreaciones innecesarias
    const refreshPermissions = useCallback(() => {
        refetchPermissions();
    }, [refetchPermissions]);

    // Función para refrescar permisos manualmente
    const refreshNotifications = useCallback(() => {
        refetchNotifications();
    }, [refetchNotifications]);

    // Función para recargar la lista de usuarios
    const refreshUsers = useCallback(async () => {
        const usersData = await fetchUsers();
        setUsers(usersData);
    }, []);

    // Función para recargar la lista de roles
    const refreshRoles = useCallback(async () => {
        const rolesData = await fetchRolesPermissions();
        setRoles(rolesData);
        refreshPermissions();
    }, [refreshPermissions]);

    // Calcular los permisos en el contexto de forma centralizada
    const permissions = useMemo(() => {
        if (!user || !userCurrentPermissions.length) {
            return {
                canNewProject1: false,
                canNewProject2: false,
                canNewUser: false,
                canNewRole: false,
                canNewTask: false,
                canEditTask: false,
                canDeleteTask: false,
                canSettlement: false,
            };
        }

        // Función auxiliar para buscar el permiso en todo el array de userCurrentPermissions.
        const hasPermission = ({
            appName,
            permissionName,
            categoryName,
        }: {
            appName: string;
            permissionName: string;
            categoryName: string;
        }): boolean =>
            userCurrentPermissions.some(user =>
                user.permissions?.some(
                    perm =>
                        perm.app_name === appName &&
                        perm.permission_name === permissionName &&
                        perm.category_name === categoryName &&
                        perm.is_active
                )
            );

        const canNewProject1 = hasPermission({
            appName: "Gestión de Proyectos",
            permissionName: "Crear Proyecto",
            categoryName: "Proyecto",
        });

        const canNewProject2 = hasPermission({
            appName: "Gestión de Proyectos",
            permissionName: "Crear Proyecto",
            categoryName: "Proyecto Mejora",
        });

        const canNewUser = hasPermission({
            appName: "Gestión de Proyectos",
            permissionName: "Crear Usuario",
            categoryName: "Seguridad",
        });

        const canNewRole = hasPermission({
            appName: "Gestión de Proyectos",
            permissionName: "Crear Rol",
            categoryName: "Seguridad",
        });

        const canNewTask = hasPermission({
            appName: "Gestión de Proyectos",
            permissionName: "Crear Tarea",
            categoryName: "Tarea",
        });

        const canEditTask = hasPermission({
            appName: "Gestión de Proyectos",
            permissionName: "Editar Tarea",
            categoryName: "Tarea",
        });

        const canDeleteTask = hasPermission({
            appName: "Gestión de Proyectos",
            permissionName: "Eliminar Tarea",
            categoryName: "Tarea",
        });

        const canSettlement = hasPermission({
            appName: "TMS",
            permissionName: "Acceso Liquidación",
            categoryName: "Logística",
        });

        return {
            canNewProject1,
            canNewProject2,
            canNewUser,
            canNewRole,
            canNewTask,
            canEditTask,
            canDeleteTask,
            canSettlement,
        };
    }, [user, userCurrentPermissions]);


    // Memoizar el valor del contexto para evitar renderizados innecesarios
    const contextValue = useMemo(() => ({
        user,
        setUser,
        notifications,
        setNotifications,
        users,
        setUsers,
        refreshUsers,
        roles,
        setRoles,
        refreshRoles,
        userCurrentPermissions,
        permissionsLoaded,
        permissions,
    }), [user, notifications, users, roles, refreshUsers, refreshRoles, userCurrentPermissions, permissionsLoaded, permissions]);

    return (
        <UserContext.Provider value={contextValue}>
            {children}
        </UserContext.Provider>
    );
};

/**
 * Hook personalizado para acceder al contexto del usuario.
 * 
 * @throws Error si se usa fuera de un `UserProvider`.
 * @returns El contexto del usuario.
 */
export const useUser = (): UserContextType => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser debe ser usado dentro de un UserProvider');
    }
    return context;
};
